import type { InsertAttestationConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Public input for the `insertAttestationConfig` mutation. Mirrors the legacy
 * `InsertAttestationConfigMutationVariables['object']` (the
 * `InsertAttestationConfigInput`) shape so the flag-gated wrapper can forward
 * the same object to either path.
 */
export interface InsertAttestationConfigInput {
  ParentId: string;
  RequireGlobalAttestation: boolean;
  AttestationGroupIds: string[];
  AttestationPromptText?: string | null;
  AttestationTimeLimit?: string | null;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation response shape
 * so consumers branching on the result do not need to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  Id: string;
}): InsertAttestationConfigMutation => ({
  insertChildAttestationConfig: { Id: data.Id },
});

export const useInsertAttestationConfigTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.attestation.upsertConfig.mutationOptions({
      onSuccess: async () => {
        // The attestations Tab re-derives its view from three read families:
        // the document read (attestation config), the attestation-cycle reads,
        // and the attestation register. Invalidate all three routers so the Tab
        // reflects the upserted config after save — matching the Apollo path,
        // which re-fetched via those reads with no inline cache update.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.document.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.attestationCycle.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.attestation.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertAttestationConfig: async (
      input: InsertAttestationConfigInput
    ): Promise<InsertAttestationConfigMutation> => {
      const result = await mutation.mutateAsync({
        ParentId: input.ParentId,
        RequireGlobalAttestation: input.RequireGlobalAttestation,
        AttestationGroupIds: input.AttestationGroupIds,
        AttestationPromptText: input.AttestationPromptText,
        AttestationTimeLimit: input.AttestationTimeLimit,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
